import React from 'react';
import type { Problem, AlgorithmProblem } from '../data/problems';
import type { TestResult, PyOutput } from '../utils/codeRunner';

type TestTab = 'tests' | 'console';
type Language = 'javascript' | 'typescript' | 'python';

interface Props {
  problem: Problem;
  language: Language;
  results: TestResult[];
  running: boolean;
  pyDisabled: boolean;
  pyOutput: PyOutput | null;
  // tabs
  testTab: TestTab;
  setTestTab: (t: TestTab) => void;
  // expand
  expandedCase: number | null;
  setExpanded: (n: number | null) => void;
  // pass summary
  allPass: boolean;
  passCount: number;
  totalCount: number;
  // run / submit
  runBtnLabel: string;
  onRun: () => void;
  onSubmit: () => void;
  submitting: boolean;
  submitMsg: string;
  // custom input
  customInput: string;
  setCustomInput: (s: string) => void;
  customResult: string;
  onRunCustom: () => void;
}

// The bottom pane of the IDE: tests/console tab strip, per-case results with
// expandable detail, run + submit buttons, and the custom-input box. Extracted
// from CodeCompiler so the orchestrator stays focused on state + wiring.
const IdeTestPanel: React.FC<Props> = ({
  problem, language, results, running, pyDisabled, pyOutput,
  testTab, setTestTab, expandedCase, setExpanded,
  allPass, passCount, totalCount,
  runBtnLabel, onRun, onSubmit, submitting, submitMsg,
  customInput, setCustomInput, customResult, onRunCustom,
}) => (
  <div className="ide-test-pane">
    <div className="ide-test-header">
      <div style={{ display: 'flex', gap: 4 }}>
        <button
          className={`sk-tab ${testTab === 'tests' ? 'active' : ''}`}
          onClick={() => setTestTab('tests')}
          style={{ fontSize: 11, fontFamily: 'inherit' }}
        >
          tests ({problem.testCases.length})
          {results.length > 0 && (
            <span style={{ marginLeft: 5, fontSize: 9, fontWeight: 700, color: allPass ? 'var(--good)' : 'var(--w-coral)' }}>
              {passCount}/{totalCount}
            </span>
          )}
        </button>
        <button
          className={`sk-tab ${testTab === 'console' ? 'active' : ''}`}
          onClick={() => setTestTab('console')}
          style={{ fontSize: 11, fontFamily: 'inherit' }}
        >
          console
        </button>
      </div>
      <span style={{ flex: 1 }} />
      <button
        className="sk-btn"
        onClick={onRun}
        disabled={running || pyDisabled}
        style={{ fontSize: 11 }}
        title="Run tests (⌘↵ on mac, Ctrl+↵ on win/linux)"
      >
        {runBtnLabel}
      </button>
      <button
        className="sk-btn primary"
        style={{ fontSize: 11 }}
        onClick={onSubmit}
        disabled={!allPass || submitting}
        title={allPass ? 'Submit your solution' : 'Pass all tests first'}
      >
        {submitting ? '⏳' : 'submit ✓'}
      </button>
      {submitMsg && <span className="ide-submit-msg">{submitMsg}</span>}
    </div>

    {testTab === 'tests' && (
      <div className="ide-test-scroll">
        {results.length === 0 && !running && (
          <span className="ide-empty">
            {pyDisabled ? '⌛ loading python runtime…' : 'run your code to see test results'}
          </span>
        )}

        {results.map((r, i) => (
          <div key={i} style={{ marginBottom: 4 }}>
            <button
              onClick={() => setExpanded(expandedCase === i ? null : i)}
              className={`ide-test-row ${r.passed ? 'passed' : 'failed'}`}
            >
              {r.passed ? '✓' : '✗'}
              {' '}
              <span style={{ userSelect: 'text' }}>
                {r.kind === 'algo'
                  ? `test ${i + 1} · ${Object.entries(r.input).map(([k, v]) => `${k}=${JSON.stringify(v)}`).join(', ')} → ${r.validateName ? `validate:${r.validateName}` : JSON.stringify(r.expected)}`
                  : r.label}
              </span>
              <span className={`ide-status-pill ${r.passed ? 'passed' : 'failed'}`}>
                {r.passed ? 'passed' : 'failed'}
              </span>
              <span className="ide-caret">{expandedCase === i ? '▲' : '▼'}</span>
            </button>

            {expandedCase === i && (
              <div className="ide-test-detail">
                {r.kind === 'algo' && (
                  <>
                    <div><strong>input:</strong> {JSON.stringify(r.input)}</div>
                    {r.validateName
                      ? <div><strong>validator:</strong> {r.validateName}(input, output)</div>
                      : <div><strong>expected:</strong> {JSON.stringify(r.expected)}</div>}
                    <div style={{ color: r.error ? 'var(--w-coral)' : 'inherit' }}>
                      <strong>got:</strong>{' '}
                      {r.error
                        ? <pre style={{ display: 'inline', margin: 0, whiteSpace: 'pre-wrap', fontFamily: 'inherit' }}>{r.error}</pre>
                        : JSON.stringify(r.actual)}
                    </div>
                  </>
                )}
                {(r.kind === 'class' || r.kind === 'fullstack' || r.kind === 'agent') && r.error && (
                  <pre style={{ margin: 0, whiteSpace: 'pre-wrap', fontFamily: 'inherit', color: 'var(--w-coral)' }}>{r.error}</pre>
                )}
                {(r.stdout || r.stderr) && (
                  <div style={{ marginTop: 4, paddingTop: 4, borderTop: '1px dashed var(--paper-3)' }}>
                    <div style={{ color: 'var(--ink-faint)', fontSize: 9, marginBottom: 2 }}>
                      console (this test case only)
                    </div>
                    {r.stdout && (
                      <pre style={{ margin: 0, whiteSpace: 'pre-wrap', fontFamily: 'inherit', color: 'var(--ink-soft)' }}>{r.stdout}</pre>
                    )}
                    {r.stderr && (
                      <pre style={{ margin: 0, whiteSpace: 'pre-wrap', fontFamily: 'inherit', color: 'var(--w-amber)' }}>stderr: {r.stderr}</pre>
                    )}
                  </div>
                )}
                {r.kind === 'class' && !r.error && (
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 10 }}>
                    <thead>
                      <tr style={{ color: 'var(--ink-faint)' }}>
                        <th style={{ textAlign: 'left', padding: '2px 4px' }}>op</th>
                        <th style={{ textAlign: 'left', padding: '2px 4px' }}>args</th>
                        <th style={{ textAlign: 'left', padding: '2px 4px' }}>expected</th>
                        <th style={{ textAlign: 'left', padding: '2px 4px' }}>got</th>
                      </tr>
                    </thead>
                    <tbody>
                      {r.ops.map((op, j) => (
                        <tr key={j} style={{ background: op.passed ? 'transparent' : 'rgba(196,90,58,0.08)' }}>
                          <td style={{ padding: '2px 4px' }}>{op.op}</td>
                          <td style={{ padding: '2px 4px' }}>{JSON.stringify(op.args)}</td>
                          <td style={{ padding: '2px 4px' }}>{JSON.stringify(op.expected)}</td>
                          <td style={{ padding: '2px 4px', color: op.passed ? 'inherit' : 'var(--w-coral)' }}>{JSON.stringify(op.actual)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
                {r.kind === 'fullstack' && !r.error && (
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 10 }}>
                    <thead>
                      <tr style={{ color: 'var(--ink-faint)' }}>
                        <th style={{ textAlign: 'left', padding: '2px 4px' }}>request</th>
                        <th style={{ textAlign: 'left', padding: '2px 4px' }}>status</th>
                        <th style={{ textAlign: 'left', padding: '2px 4px' }}>response body</th>
                      </tr>
                    </thead>
                    <tbody>
                      {r.steps.map((s, j) => (
                        <tr key={j} style={{ background: s.passed ? 'transparent' : 'rgba(196,90,58,0.08)' }}>
                          <td style={{ padding: '2px 4px', whiteSpace: 'nowrap' }}>
                            <span className="sk-pill amber" style={{ fontSize: 9 }}>{s.method}</span> {s.path}
                          </td>
                          <td style={{ padding: '2px 4px', color: (s.expectedStatus != null && s.actualStatus !== s.expectedStatus) ? 'var(--w-coral)' : 'inherit' }}>
                            {s.actualStatus ?? '—'}{s.expectedStatus != null ? ` (exp ${s.expectedStatus})` : ''}
                          </td>
                          <td style={{ padding: '2px 4px', color: s.passed ? 'inherit' : 'var(--w-coral)' }}>
                            {JSON.stringify(s.actual)}
                            {s.expected !== undefined && !s.passed ? ` · exp ${JSON.stringify(s.expected)}` : ''}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
                {r.kind === 'agent' && !r.error && (
                  <>
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 10 }}>
                      <thead>
                        <tr style={{ color: 'var(--ink-faint)' }}>
                          <th style={{ textAlign: 'left', padding: '2px 4px' }}>#</th>
                          <th style={{ textAlign: 'left', padding: '2px 4px' }}>tool call</th>
                          <th style={{ textAlign: 'left', padding: '2px 4px' }}>arguments</th>
                          <th style={{ textAlign: 'left', padding: '2px 4px' }}>expected</th>
                        </tr>
                      </thead>
                      <tbody>
                        {r.calls.length === 0 && (
                          <tr><td colSpan={4} style={{ padding: '2px 4px', color: 'var(--ink-faint)' }}>no tool calls recorded</td></tr>
                        )}
                        {r.calls.map((c, j) => (
                          <tr key={j} style={{ background: c.passed ? 'transparent' : 'rgba(196,90,58,0.08)' }}>
                            <td style={{ padding: '2px 4px' }}>{c.index + 1}</td>
                            <td style={{ padding: '2px 4px', color: c.passed ? 'inherit' : 'var(--w-coral)' }}>
                              <span className="sk-pill amber" style={{ fontSize: 9 }}>{c.name ?? '—'}</span>
                            </td>
                            <td style={{ padding: '2px 4px' }}>{JSON.stringify(c.arguments)}</td>
                            <td style={{ padding: '2px 4px', color: 'var(--ink-faint)' }}>
                              {c.expectedName != null && c.expectedName !== c.name ? `exp ${c.expectedName}` : ''}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                    <div style={{ marginTop: 4, paddingTop: 4, borderTop: '1px dashed var(--paper-3)' }}>
                      <strong>final answer:</strong>{' '}
                      <span style={{ color: r.passed ? 'inherit' : 'var(--w-coral)' }}>{JSON.stringify(r.finalAnswer)}</span>
                      {r.expectedFinal !== undefined && r.expectedFinal !== null && !r.passed && (
                        <span style={{ color: 'var(--ink-faint)' }}> · exp {JSON.stringify(r.expectedFinal)}</span>
                      )}
                    </div>
                  </>
                )}
              </div>
            )}
          </div>
        ))}

        {/* Custom test — JS/TS algo only */}
        {(language === 'javascript' || language === 'typescript') && problem.type === 'algorithm' && (
          <div style={{ marginTop: 8, paddingTop: 8, borderTop: '1px dashed var(--paper-3)' }}>
            <div style={{ fontFamily: 'JetBrains Mono, monospace', fontSize: 10, color: 'var(--ink-faint)', marginBottom: 4 }}>custom input</div>
            <textarea
              rows={2}
              placeholder={`e.g. ${JSON.stringify((problem as AlgorithmProblem).testCases[0]?.input)}`}
              value={customInput}
              onChange={e => setCustomInput(e.target.value)}
              className="ide-custom-input"
            />
            <button className="sk-btn" style={{ fontSize: 10, marginTop: 4 }} onClick={onRunCustom}>run custom</button>
            {customResult && (
              <pre style={{ margin: '4px 0 0', fontFamily: 'JetBrains Mono, monospace', fontSize: 10, color: 'var(--ink-soft)' }}>
                {customResult}
              </pre>
            )}
          </div>
        )}
      </div>
    )}

    {testTab === 'console' && (
      <div className="ide-test-scroll">
        <div className="ide-empty" style={{ fontSize: 9, marginBottom: 6 }}>
          tip: per-test-case prints show under each expanded test in the tests tab
        </div>
        {pyOutput ? (
          <>
            {pyOutput.stdout && <pre className="ide-pre-mono stdout">{pyOutput.stdout}</pre>}
            {pyOutput.stderr && <pre className="ide-pre-mono stderr">stderr: {pyOutput.stderr}</pre>}
            {pyOutput.error && <pre className="ide-pre-mono errmsg">error: {pyOutput.error}</pre>}
            {!pyOutput.stdout && !pyOutput.stderr && !pyOutput.error && (
              <span className="ide-empty" style={{ fontStyle: 'italic' }}>no top-level console output</span>
            )}
          </>
        ) : (
          <span className="ide-empty" style={{ fontStyle: 'italic' }}>no output yet</span>
        )}
      </div>
    )}
  </div>
);

export default IdeTestPanel;
